import { LogArgument } from 'rollbar';
import { DEVICE_TYPE } from './constants';
import getSessionId from './getSessionId';
import {
  apiPostEvent,
  generateEventRequestPayload,
} from './utils/apiPostEvent';
import Rollbar from './utils/rollbar';
import { isInWebView } from './webViewBridge/utils';

const eventNameMapping = {
  sponsored_ads_showed: 'impression',
  sponsored_ad_clicked: 'click',
  product_sales: 'sale',
};

interface IPaidProductParams {
  ad_id: string;
  misc?: string;
}

interface ISale {
  /** Clients should send either the `advertiser_id` or `brand` for attribution */
  advertiser_id: string;
  brand: string;
  quantity: number;
  unit_price: number;
  product_id: string;
}

interface ISaleParams {
  sales: ISale[];
  order_id?: string;
}

const logEvent = async (
  eventName: keyof typeof eventNameMapping,
  params: IPaidProductParams & ISaleParams,
): Promise<unknown> => {
  try {
    if (isInWebView()) {
      logEventWebViewBridge(eventName, params);
      return;
    }

    const sessionId = getSessionId() ?? 'unknown';

    switch (eventName) {
      case 'product_sales': {
        const requestPayload = generateEventRequestPayload({
          event_type: 'sale',
          sales: params.sales,
          order_id: params.order_id,
          session_id: sessionId,
          device_type: DEVICE_TYPE,
        });
        apiPostEvent(requestPayload);
        return;
      }

      case 'sponsored_ads_showed':
      case 'sponsored_ad_clicked': {
        const requestPayload = generateEventRequestPayload({
          event_type: eventNameMapping[eventName],
          ad_id: params.ad_id,
          session_id: sessionId,
          device_type: DEVICE_TYPE,
          misc: params.misc,
        });
        apiPostEvent(requestPayload);
        return;
      }
      default: {
        // Ensure all new event names added to the eventNameMapping are also handled in the switch case below.
        // TypeScript will flag an error for any unhandled event names, enforcing exhaustive handling.
        const exhaustiveCheck: never = eventName;
        Rollbar.warning(
          `[logEvent] eventName ${exhaustiveCheck} is not handled`,
        );
      }
    }
  } catch (error) {
    Rollbar.warning(error as LogArgument);
  }
};

function logEventWebViewBridge(
  eventName: keyof typeof eventNameMapping,
  params: IPaidProductParams & ISaleParams,
) {
  if (!window.rmnMobileSdk) {
    Rollbar.warning('[logEventWebViewBridge] rmnMobileSdk is not available');
    return;
  }
  const webViewBridge = window.rmnMobileSdk;
  switch (eventName) {
    case 'sponsored_ads_showed':
      webViewBridge.logProductAdImpression(params.ad_id, params.misc ?? '');
      break;
    case 'sponsored_ad_clicked':
      webViewBridge.logProductAdClick(params.ad_id, params.misc ?? '');
      break;
    case 'product_sales':
      webViewBridge.logProductAdSales(
        JSON.stringify(params.sales),
        params.order_id ?? '',
      );
      break;
    default: {
      // Ensure all new event names added to the eventNameMapping are also handled in the switch case below.
      // TypeScript will flag an error for any unhandled event names, enforcing exhaustive handling.
      const exhaustiveCheck: never = eventName;
      Rollbar.warning(
        `[logEventWebViewBridge] eventName ${exhaustiveCheck} is not handled`,
      );
    }
  }
}

export default logEvent;
